import { DEV, onCleanup, onMount } from 'solid-js'
import Stats from 'stats.js'
import { DepthTexture, MeshLambertMaterial, SRGBColorSpace, WebGLRenderTarget } from 'three'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import createDraftUnitMovesUnitEffect from '../../rx/effect/createDraftUnitMovesUnitEffect'
import createDraftUnitRotatesTurretEffect from '../../rx/effect/createDraftUnitRotatesTurretEffect'
import { useSharedManhattanDistanceToLandListMemo } from '../../rx/memo/useSharedManhattanDistanceToLandListMemo'
import { useSharedMapControlTargetEntsListMemo } from '../../rx/memo/useSharedMapControlTargetEntsListMemo'
import { useSharedTerrainBaseFloorElevationListMemo } from '../../rx/memo/useSharedTerrainBaseFloorElevationListMemo'
import { useSharedTerrainDerivedFloorElevationListMemo } from '../../rx/memo/useSharedTerrainDerivedFloorElevationListMemo'
import { useSharedUnitEntsListMemo } from '../../rx/memo/useSharedUnitEntsListMemo'
import { useEntityByEntityTypeIdReactiveMap } from '../../rx/reactive_map/useEntityByEntityTypeIdReactiveMap'
import { useEngineModeSignal } from '../../rx/signal/useEngineModeSignal'
import { useWaterMeshList } from '../../rx/use/useWaterMeshList'
import bindEngineToKeyboard from '../bindEngineToKeyboard'
import { Engine } from '../core/engine/Engine.type'
import { EngineMode } from '../core/engine/EngineMode/EngineMode.enum'
import applyCameraRotationInFrame from '../core/entity/EntityHeading/applyCameraRotationInFrame'
import { getRoadEntsFromMap } from '../core/entity/entityTypeMetaList.generated'
import toCoord from '../core/tile_position_xy/toCoord'
import { useProgressArrowKeyPan } from '../core/useProgressArrowKeyPan'
import registerContextMenuEvent from '../dom/event/registerContextMenuEvent'
import registerGamePlayKeyboardEvents from '../dom/event/registerGamePlayKeyboardEvents'
import registerKeyDownEvent from '../dom/event/registerKeyDownEvent'
import registerKeyUpEvent from '../dom/event/registerKeyUpEvent'
import registerMouseDownEvent from '../dom/event/registerMouseDownEvent'
import registerMouseUpEvent from '../dom/event/registerMouseUpEvent'
import registerPointerMoveEvent from '../dom/event/registerPointerMoveEvent'
import { dispatchGameObject3DFrameEvent } from '../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate'
import onWindowEvent from '../onWindowEvent'
import addOnScreenCompassToMapControlsEvents from '../three/camera/addOnScreenCompassToMapControlsEvents'
import addDebugMapCornerSprites from '../three/EntityThreeModel/addDebugMapCornerSprites'
import createBombardCursorGroup from '../three/fn/createBombardCursorGroup'
import createDraftMoveObject3D from '../three/fn/createDraftMoveObject3D'
import createEntityThreeModelCollectionV2 from '../three/fn/createEntityThreeModelCollectionV2'
import createFogExp2 from '../three/fn/createFogExp2'
import createHoverPositionMesh from '../three/fn/createHoverPositionMesh'
import createMapEditorEdgeWallsGroupV2 from '../three/fn/createMapEditorEdgeWallsGroupV2'
import createPaintedTileOptionEntityThreeModelCollection from '../three/fn/createPaintedTileOptionEntityThreeModelCollection'
import createSelectedPositionMesh from '../three/fn/createSelectedPositionMesh'
import createThreeMapControls from '../three/fn/createThreeMapControls'
import createThreeOrbitControls from '../three/fn/createThreeOrbitControls'
import createUnloadCargoTargetPositionObject3D from '../three/fn/createUnloadCargoTargetPositionObject3D'
import { setEngineThreeControls } from '../three/fn/getSetEngineThreeControls'
import { getEngineThreeMainCamera } from '../three/fn/getSetEngineThreeMainCamera'
import { getEngineThreeMainRenderer } from '../three/fn/getSetEngineThreeMainRenderer'
import useEffectComposer from '../three/fn/useEffectComposer'
import useGetTileNodeFromPointer from '../three/fn/useGetTileNodeIntersectionFromPointer'
import useMainScene from '../three/fn/useMainScene'
import usePointerRefVector2 from '../three/fn/usePointerRefVector2'
import createBoulderInstancedMeshV2QuaterniusEffect from '../three/objects/BoulderV2/createBoulderInstancedMeshV2QuaterniusEffect'
import createGrassWavesEffect from '../three/objects/DetailProps/GrassWaves/createGrassWavesEffect'
import createMountainTerrainEffectV2 from '../three/objects/Mountain/createMountainTerrainEffectV2'
import createTerrainMesh1V3Effect from '../three/objects/TerrainMesh1V3/createTerrainMesh1V3Effect'
import createDeadwoodTerrainEffect from '../three/objects/Trees/Deadwood/createDeadwoodTerrainEffect'
import createForestTerrainEffect from '../three/objects/Trees/Forest/createForestTerrainEffect'
import createJungleTerrainEffect from '../three/objects/Trees/Jungle/createJungleTerrainEffect'
import createSwampTerrainEffect from '../three/objects/Trees/Swamp/createSwampTerrainEffect'
import createWaterPlaneEffect from '../three/objects/WaterPlane/createWaterPlaneEffect'
import { RenderOrder } from '../three/RenderOrder.enum'
import createMountedDepthColorEffectShader from '../three/shader/createMountedDepthColorEffectShader'
import { createAmbientLight } from '../three/sky/createAmbientLight'
import { createHemisphereLight } from '../three/sky/createHemisphereLight'
import { useSharedDirectionalLight } from '../three/sky/useSunDirectionalLight'
import createGradientTextureSkyV2 from '../three/sky/createGradientTextureSkyV2'

const stats = new Stats()
stats.showPanel(0) // 0: fps, 1: ms, 2: mb, 3+: custom
document.body.appendChild(stats.dom)

export default function bindEngineToComponentV2(
  engine: Engine,
  canvasRef: HTMLCanvasElement,
): void {

  const [getEngineMode] = useEngineModeSignal(engine)

  const renderer = getEngineThreeMainRenderer(engine)
  const camera = getEngineThreeMainCamera(engine)

  const isMapPreview = getEngineMode() === EngineMode.MapPreview
  const isArmyPainter = getEngineMode() === EngineMode.ArmyPainter
  const pointerRef = usePointerRefVector2(engine)
  const scene = useMainScene(engine)

  onMount(() => {
    // Set up the scene, camera, and renderer
    const mapControls = isArmyPainter
      ? createThreeOrbitControls(canvasRef, camera)
      : createThreeMapControls(engine, canvasRef, camera)
    setEngineThreeControls(engine, mapControls)

    // createSingleColorSky(scene)
    // createGradientTextureSky(scene)
    // createGoldenHourTextureSky(scene)
    createGradientTextureSkyV2(scene)

    scene.add(createHemisphereLight())
    scene.add(createAmbientLight())
    scene.add(useSharedDirectionalLight(engine))

    // scene.add(createOceanFloorPlaneMesh())
    createWaterPlaneEffect(engine, scene)

    const entityByTypeMap = useEntityByEntityTypeIdReactiveMap(engine)
    const getUnitEntityList = useSharedUnitEntsListMemo(engine)
    const getMapControlTargetEntsList = useSharedMapControlTargetEntsListMemo(engine)

    // const getEntityList = () => engine.state.ents.filter((ent) => {
    //   return !isEntityTerrainMesh1(ent) && !isEntityMountain(ent) && !isEntityRiver(ent) && !isEntityShoal(ent)
    // })
    // const unitEntityThreeModelCollection = createEntityThreeModelCollectionV2(engine, scene, getEntityList, 'Ents')
    const propertyEntsCollection = createEntityThreeModelCollectionV2(engine, scene, getMapControlTargetEntsList, 'PropertyEnts')
    // propertyEntityThreeModelCollection.group.visible = false
    const unitEntityThreeModelCollection = createEntityThreeModelCollectionV2(engine, scene, getUnitEntityList, 'UnitEnts')
    const roadEntsCollection = createEntityThreeModelCollectionV2(engine, scene, () => getRoadEntsFromMap(entityByTypeMap), 'RoadEnts')

    propertyEntsCollection.group.renderOrder = RenderOrder.PropertyEntity
    unitEntityThreeModelCollection.group.renderOrder = RenderOrder.UnitEntity
    roadEntsCollection.group.renderOrder = RenderOrder.RoadEntity

    // unitEntityThreeModelCollection.group.visible = false
    createDraftUnitRotatesTurretEffect(engine, unitEntityThreeModelCollection)
    createDraftUnitMovesUnitEffect(engine, unitEntityThreeModelCollection)

    createPaintedTileOptionEntityThreeModelCollection(engine, scene)

    // createTerrainMesh1Effect(engine, scene)
    // createTerrainMesh1V2Effect(engine, scene)
    createTerrainMesh1V3Effect(engine, scene)
    // createTerrainSeaSurfaceEffect(engine, scene)
    // createTerrainRiverSurfaceEffect(engine, scene)
    createMountainTerrainEffectV2(engine, scene)
    createForestTerrainEffect(engine, scene)
    createJungleTerrainEffect(engine, scene)
    createSwampTerrainEffect(engine, scene)
    createDeadwoodTerrainEffect(engine, scene)
    createBoulderInstancedMeshV2QuaterniusEffect(engine, scene)
    // createGrassDetailPropsV2Effect(engine, scene)
    // createGrassWavesObject(engine, scene)
    createGrassWavesEffect(engine, scene)
    createFogExp2(scene)
    // const animateSenehaBelkhaleFog = createSenehaBelkhaleFog(scene)


    const getTileNodeIntersectionFromPointer = useGetTileNodeFromPointer(engine)

    const updateHoverTilePosition = createHoverPositionMesh(engine, scene)
    createSelectedPositionMesh(engine, scene)
    createBombardCursorGroup(engine, scene)
    createUnloadCargoTargetPositionObject3D(engine, scene, unitEntityThreeModelCollection)

    bindEngineToKeyboard(engine)
    registerPointerMoveEvent(engine, canvasRef)
    registerMouseDownEvent(engine, canvasRef)
    registerMouseUpEvent(engine, canvasRef)
    registerContextMenuEvent(engine)
    registerGamePlayKeyboardEvents(engine)
    // registerMouseClickEvent(engine)

    registerKeyUpEvent(engine)
    registerKeyDownEvent(engine)

    if (!isMapPreview) {
      createDraftMoveObject3D(engine)
    }

    camera.userData.heading = 0

    createMapEditorEdgeWallsGroupV2(engine)

    addOnScreenCompassToMapControlsEvents(engine)

    // Animation loop
    let start: number = performance.now()
    let dt: number = 0
    let at: number = 0

    const composer = useEffectComposer(engine)
    const waterRenderTarget = new WebGLRenderTarget(renderer.domElement.width, renderer.domElement.height, {
      depthTexture: new DepthTexture(renderer.domElement.width, renderer.domElement.height),
      colorSpace: SRGBColorSpace,
    })

    const waterMeshList = useWaterMeshList(engine)

    const depthColorEffect = createMountedDepthColorEffectShader(composer, camera)
    // createMountedSaturationShader(composer)
    // createMountedPixelateShader(composer)

    // const filmGrainShader = new ShaderPass(createFilmGrainShader())
    // composer.addPass(filmGrainShader)
    // const lensDistortionShader = new ShaderPass(createLensDistortionShader())
    // composer.addPass(lensDistortionShader)

    // neat trick to see the depth buffer
    // scene.overrideMaterial = new MeshDepthMaterial()

    function onWindowResize() {
      const { domElement } = renderer
      const { width: domWidth, height: domHeight } = domElement
      // composer.setSize(window.innerWidth, window.innerHeight)
      // pixelateShaderEffect.uniforms.resolution.value.set(domWidth, domHeight)
      // depthSaturation.uniforms.resolution.value.set(domWidth, domHeight)
      composer.setSize(domWidth, domHeight)
      composer.passes.forEach((pass) => {
        if (pass instanceof ShaderPass) {
          const { resolution } = pass.uniforms
          if (resolution) {
            resolution.value.set(domWidth, domHeight)
          }
        }
      })
      composer.renderTarget1.depthTexture = new DepthTexture(domWidth, domHeight)
      composer.renderTarget2.depthTexture = new DepthTexture(domWidth, domHeight)

      waterRenderTarget.setSize(domWidth, domHeight)
      waterRenderTarget.depthTexture = new DepthTexture(domWidth, domHeight)
    }
    onWindowEvent('resize', onWindowResize)
    onMount(onWindowResize)

    onMount(() => {
      const last = composer.passes.at(-1)
      if (last) {
        last.renderToScreen = true
      }
      depthColorEffect.uniforms.tDepth.value = composer.readBuffer.depthTexture
    })

    useSharedTerrainBaseFloorElevationListMemo(engine)
    useSharedTerrainDerivedFloorElevationListMemo(engine)
    useSharedManhattanDistanceToLandListMemo(engine)
    const progressArrowKeyPan = useProgressArrowKeyPan(engine)

    const onFrameList = engine.ofELL()
    // const queueList = useEngineQueueList(engine)

    const render: FrameRequestCallback = (newTimestampMs: number) => {
      stats.begin()

      at = newTimestampMs
      dt = at - start
      start = at

      const pointerIntersection = getTileNodeIntersectionFromPointer(pointerRef)
      const pointerTile = pointerIntersection?.object?.userData?.tile
      const pointerTilePosition = pointerTile ? toCoord(pointerTile) : null

      if (!isMapPreview) {
        updateHoverTilePosition(pointerTilePosition)
      }
      mapControls.update()
      progressArrowKeyPan(dt, camera, mapControls)
      applyCameraRotationInFrame(camera, mapControls, dt)
      dispatchGameObject3DFrameEvent(onFrameList, dt, at)
      // soft-cap the queue at 16ms (60fps) to reduce frame time variance
      // runEngineQueueListWithBudget(queueList, 3)
      // animateSenehaBelkhaleFog(dt)

      // depthSaturation.uniforms.tDepth.value = composer.readBuffer.depthTexture
      // depthSaturation.uniforms.tDepth.value = renderTarget.depthTexture

      if (waterMeshList.length > 0) {
        const prevRenderTarget = renderer.getRenderTarget()

        // hide water for prepass
        const prevVisible: boolean[] = []
        for (let i = 0; i < waterMeshList.length; i++) {
          const mesh = waterMeshList[i]
          prevVisible[i] = mesh.visible
          mesh.visible = false
        }

        renderer.setRenderTarget(waterRenderTarget)
        renderer.clear()
        renderer.render(scene, camera)

        // restore visibility
        for (let i = 0; i < waterMeshList.length; i++) {
          waterMeshList[i].visible = prevVisible[i]
        }
        renderer.setRenderTarget(prevRenderTarget)

        const { domElement } = renderer
        const { width: domWidth, height: domHeight } = domElement

        for (const { material } of waterMeshList) {
          const shader = (material as MeshLambertMaterial).userData.waterV7LambertShader
          if (!shader) continue
          const { uniforms } = shader
          uniforms.u_depthTexture.value = waterRenderTarget.depthTexture
          uniforms.u_winRes.value.set(domWidth, domHeight)
          uniforms.u_near.value = camera.near
          uniforms.u_far.value = camera.far
        }
      }

      depthColorEffect.uniforms.tDepth.value = composer.readBuffer.depthTexture

      // renderer.render(scene, camera)
      composer.render(dt)

      stats.end()

      animationId = requestAnimationFrame(render)
    }

    let animationId = requestAnimationFrame(render)

    onCleanup(() => {
      cancelAnimationFrame(animationId)
    })

    if (DEV) {
      setTimeout(() => {
        if (renderer) {
          console.log('renderer.info.render.triangles', renderer.info.render.triangles)
        }

        // // TODO what materials are in my scene?
        // const materialCounts: Record<string, number> = {}
        // scene.traverse((object) => {
        //   const objMaterial : Array<Material> | Material | null | undefined = (object as unknown as {material: Material | null}).material
        //   if (objMaterial) {
        //     // if (objMaterial instanceof MeshStandardMaterial) {
        //     //   console.log('MeshStandardMaterial.obj', object.name || object)
        //     // }
        //     // console.log('object.material', objMaterial)
        //     const materials = Array.isArray(objMaterial) ? objMaterial : [objMaterial]
        //     materials.forEach((material) => {
        //       const materialType = material.type || 'Unknown Type'
        //       materialCounts[materialType] = (materialCounts[materialType] || 0) + 1
        //     })
        //   }
        // })
        // console.table(materialCounts)

      }, 2500)
    }

    // createMountedInverval(() => {
    //   scene.traverse((obj) => {
    //     if (obj instanceof Mesh) {
    //       // object.material.needsUpdate = true
    //       obj.renderOrder = getRandomInteger(0, 100)
    //       console.log('obj.renderOrder', obj.name, obj.renderOrder)
    //       obj.material.depthTest = true
    //       obj.material.depthWrite = true
    //       obj.material.needsUpdate = true
    //     }
    //   })
    // }, 2000)

    // console.log(scene)
    // console.table(scene.children.map(obj => ({type: obj.type, name: obj.name, renderOrder: obj.renderOrder})))

    addDebugMapCornerSprites(engine)
  })
}


